import { ActionIcon, Avatar, Button, Center, Checkbox, Group, Pagination, Table, Text, Tooltip } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRobotListWithMembers } from '@mygpt/app/collaborate';
import { AiInfoItemWithMembers, ShareSource } from '@mygpt/app/collaborate/services';
import { getResourcesPrinciple, ResourceDataType } from 'packages/app/apis/services/admin';

import { NoDataTip } from './NoDataTip';
import TransferModal from './TransferModal';
import { SvgEditNew } from '@/components/Common/icon';
import { LoadingSkeleton } from '@/components/Common/LoadingSkeleton';
import UserSelectorModal from './UserSelectorModal';
import { useAdminStore } from '@/store/admin';
import { useGPTStore } from '@/store';

const RobotList = ({ shareSource }: { shareSource?: ShareSource }) => {
  const { t } = useTranslation();

  const { data: robotList, loadList, loading, page, setPage } = useRobotListWithMembers();
  const { fetchAllAdminData } = useAdminStore();
  const { assetScope } = useGPTStore();
  const [modalOpened, setModalOpened] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<AiInfoItemWithMembers | null>(null);
  const [resourcePrincipleMap, setResourcePrincipleMap] = useState<Record<string, any[]>>({});

  // 多选状态
  const [selectedRobotIds, setSelectedRobotIds] = useState<Set<string>>(new Set());
  const [transferModalOpened, setTransferModalOpened] = useState(false);

  const isLocalMode = import.meta.env.VITE_MODE === 'local';

  useEffect(() => {
    loadList({ page: 1 });
  }, []);

  useEffect(() => {
    if (!isLocalMode) return;

    const dataIds = robotList.map((item: AiInfoItemWithMembers) => item.ai.id);

    if (dataIds.length) {
      getResourcesPrinciple({
        dataIds,
        dataType: 'BOT' as ResourceDataType,
        authorityCode: 'BOT_EDIT',
      }).then((res) => {
        const data = res.data || res;
        const map: Record<string, any[]> = {};
        const userMap = Object.fromEntries((data.users || []).map((u: any) => [u.id, u]));
        Object.entries(data.resourceDataIdToUserIdsMap || {}).forEach(([dataId, userIds]) => {
          map[dataId] = (userIds as string[]).map((uid) => userMap[uid]).filter(Boolean);
        });
        setResourcePrincipleMap(map);
      });
    }
  }, [isLocalMode, robotList, page.page]);

  const handleEdit = (item: AiInfoItemWithMembers) => {
    // 每次打开弹窗，都重新拉取最新的管理员数据
    // 因为当前组织权限用的数据和选择框的数据不在一起，两边不同步。
    // 后续应该使用统一数据。
    // 先简单重新请求数据来获取可能在组织权限那边删除后的最新数据。
    fetchAllAdminData(true);
    setSelectedItem(item);
    setModalOpened(true);
  };

  const handleModalClose = () => {
    setModalOpened(false);
    setSelectedItem(null);
  };

  const handleRefresh = () => {
    loadList({ page: page.page });
  };

  // 多选处理
  const handleSelectRobot = (id: string, checked: boolean) => {
    setSelectedRobotIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRobotIds(new Set(robotList.map((item) => item.ai.id)));
    } else {
      setSelectedRobotIds(new Set());
    }
  };

  const selectedRobots = robotList.filter((item) => selectedRobotIds.has(item.ai.id));
  const isAllSelected = robotList.length > 0 && selectedRobotIds.size === robotList.length;
  const isSomeSelected = selectedRobotIds.size > 0 && !isAllSelected;

  // 判断是否为组织模式：assetScope 不为 null 时表示组织模式
  const isOrganizationMode = assetScope !== null;

  const handleOpenTransferModal = () => {
    setTransferModalOpened(true);
  };

  const handleCloseTransferModal = () => {
    setTransferModalOpened(false);
  };

  const handleTransferSuccess = () => {
    setSelectedRobotIds(new Set());
    handleRefresh();
  };

  if (loading) return <LoadingSkeleton />;
  if (!robotList.length) return <NoDataTip />;

  return (
    <>
      {/* 工具栏：转让按钮（仅在组织模式下显示） */}
      {isOrganizationMode && (
        <Group m={10} mr={0} justify="end">
          {robotList.length > 0 && (
            <Button
              variant="filled"
              color="violet"
              onClick={handleOpenTransferModal}
              disabled={selectedRobotIds.size === 0}
            >
              {t('access.transfer.button')}
            </Button>
          )}
        </Group>
      )}

      <Table verticalSpacing="sm" horizontalSpacing="md" withColumnBorders borderColor="gray.1">
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={50}>
              <Checkbox
                checked={isAllSelected}
                indeterminate={isSomeSelected}
                onChange={(e) => handleSelectAll(e.currentTarget.checked)}
                aria-label={t('access.table.select_all')}
              />
            </Table.Th>
            <Table.Th>
              <Text>{t('access.table.title.robot_name')}</Text>
            </Table.Th>
            <Table.Th className="max-w-[700px]">
              <Text>{t('access.table.title.desc')}</Text>
            </Table.Th>
            <Table.Th className="w-[180px]">
              <Text>{t('access.table.title.members')}</Text>
            </Table.Th>
            <Table.Th className="w-[100px]">
              <Text ta="center">{t('access.table.title.action')}</Text>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {robotList.map((item: AiInfoItemWithMembers, index: number) => {
            let members = item.members;
            const id = item.ai.id;
            const name = item.ai.name;
            const desc = item.ai.discrible;

            if (isLocalMode && resourcePrincipleMap[id]) {
              members = resourcePrincipleMap[id];
            }

            return (
              <Table.Tr key={index} className="hover:bg-gray-50 transition-all">
                <Table.Td>
                  <Checkbox
                    checked={selectedRobotIds.has(id)}
                    onChange={(e) => handleSelectRobot(id, e.currentTarget.checked)}
                    aria-label={t('access.table.select_robot', { name })}
                  />
                </Table.Td>
                <Table.Td>
                  <Tooltip
                    label={name}
                    position="bottom-start"
                    arrowOffset={10}
                    multiline
                    withArrow
                    className="max-w-[500px] break-all"
                  >
                    <Text c="gray.7" lineClamp={1} w={!isLocalMode ? 500 : 'auto'}>
                      {name}
                    </Text>
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  <Tooltip label={desc} multiline withArrow className="max-w-[500px] break-all">
                    <Text c="gray.7" lineClamp={1} w={!isLocalMode ? 500 : 'auto'}>
                      {desc}
                    </Text>
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  <Avatar.Group>
                    {members.map((memberItem: any, idx: number) => {
                      const user = memberItem.member || memberItem;
                      return (
                        idx < 7 && (
                          <Tooltip label={user.email} withArrow key={Math.random()}>
                            <Avatar key={user.user_id || user.id} src={user.picture} radius="xl" size="sm" />
                          </Tooltip>
                        )
                      );
                    })}
                    {members.length > 7 && (
                      <Avatar radius="xl" size="sm">
                        <span className="font-normal">+{members.length - 7}</span>
                      </Avatar>
                    )}
                  </Avatar.Group>
                </Table.Td>
                <Table.Td>
                  <Group justify="center">
                    <Tooltip label={t('access.table.edit')}>
                      <ActionIcon size="sm" variant="white" onClick={() => handleEdit(item)}>
                        <SvgEditNew />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>

      {page.pages > 1 && (
        <Center className="mt-0 py-3">
          <Pagination
            value={page.page}
            size="sm"
            onChange={(value) => {
              setPage({ page: value });
              setSelectedRobotIds(new Set());
            }}
            total={page.pages}
            color="violet.5"
          />
        </Center>
      )}

      <UserSelectorModal
        opened={modalOpened}
        onClose={handleModalClose}
        selectedItem={selectedItem}
        type="robot"
        onRefresh={handleRefresh}
        shareSource={shareSource}
      />

      <TransferModal
        opened={transferModalOpened}
        onClose={handleCloseTransferModal}
        selectedItems={selectedRobots}
        onRefresh={handleTransferSuccess}
      />
    </>
  );
};

export default RobotList;
